import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, ShoppingBag, Check } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const PeopleTab = () => {
  const [peopleData, setPeopleData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPeopleData();
  }, []);

  const fetchPeopleData = async () => {
    try {
      const response = await API.get('/shopping/by-person');
      setPeopleData(response.data);
    } catch (error) {
      console.error('Failed to fetch people data:', error);
      toast.error('Failed to load people data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-8">
        <h3 
          className="text-xl font-semibold"
          style={{ color: THEME.dark }}
        >
          Shopping by Person
        </h3>
        <p className="text-sm text-gray-500 mt-1">Items linked to people</p>
      </div>

      {peopleData.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.lighter,
            borderColor: THEME.border,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <Users className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No people-linked items</h4>
          <p className="text-gray-500 max-w-sm mx-auto">
            Link shopping items to people to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {peopleData.map((personData) => (
            <Card 
              key={personData.person} 
              className="overflow-hidden hover:shadow-lg transition-all duration-200"
              style={{ 
                borderColor: THEME.border,
                borderWidth: '1px',
                borderLeftWidth: '4px',
                borderLeftColor: THEME.primary,
              }}
            >
              <CardHeader className="pb-3 pt-5 px-5">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-900">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: THEME.light }}
                  >
                    <Users className="h-4 w-4" style={{ color: THEME.dark }} />
                  </div>
                  <span className="truncate">{personData.person}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="flex gap-2 mb-4">
                  <Badge 
                    variant="outline" 
                    className="text-xs font-medium"
                    style={{ 
                      borderColor: THEME.border,
                      color: THEME.dark,
                    }}
                  >
                    <ShoppingBag className="h-3 w-3 mr-1.5" />
                    {personData.needed_count} needed
                  </Badge>
                  <Badge 
                    variant="outline" 
                    className="text-xs font-medium"
                    style={{ 
                      borderColor: THEME.light,
                      color: THEME.dark,
                      backgroundColor: THEME.lighter,
                    }}
                  >
                    <Check className="h-3 w-3 mr-1.5" />
                    {personData.purchased_count} done
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  {personData.items.slice(0, 5).map((item) => (
                    <div 
                      key={item.id} 
                      className={`text-sm p-3 rounded-lg ${
                        item.status === 'purchased' 
                          ? 'line-through' 
                          : ''
                      }`}
                      style={{
                        backgroundColor: item.status === 'purchased' 
                          ? THEME.light 
                          : THEME.lighter,
                        color: item.status === 'purchased' 
                          ? THEME.dark 
                          : '#374151',
                      }}
                    >
                      <span className="font-medium">{item.name}</span>
                      {item.why_buying && (
                        <span className="text-xs block mt-1 opacity-70">
                          {item.why_buying}
                        </span>
                      )}
                    </div>
                  ))}
                  {personData.items.length > 5 && (
                    <p className="text-xs text-gray-400 pl-3 pt-1">
                      +{personData.items.length - 5} more items
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default PeopleTab;
